import { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, CircleCheck as CheckCircle2, Circle as XCircle, MessageSquare } from 'lucide-react';
import { useApi } from '../../lib/useApi';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfigPanel from './ConfigPanel';
import TeamMemberCard from './TeamMemberCard';
import MemberMissionView from './MemberMissionView';
import ObjectiveTreeView from './ObjectiveTreeView';
import type { MissionControlConfig, MissionControlCycle, Account, Contact } from '../../types';
import type { EnrichedCycle, TeamMember } from '../../pages/app/MissionControl';

interface Props {
  orgId: string;
  userId: string;
  role: string;
}

interface TeamMemberPayload {
  user_id: string;
  name: string;
  email: string;
  cycles: MissionControlCycle[];
  current_cycle: EnrichedCycle | null;
}

interface TeamResponse {
  config: MissionControlConfig | null;
  team_members: TeamMemberPayload[];
}

interface TeamMemberDetailResponse {
  config: MissionControlConfig | null;
  accounts: Account[];
  contacts: Contact[];
  cycles: MissionControlCycle[];
  current_cycle: EnrichedCycle | null;
}

interface OverviewResponse {
  config: MissionControlConfig | null;
  cycles: MissionControlCycle[];
  accounts: Account[];
  contacts: Contact[];
  contact_priorities: Record<string, string>;
  current_cycle: EnrichedCycle | null;
}

function toTeamMember(m: TeamMemberPayload): TeamMember {
  return {
    user_id: m.user_id,
    name: m.name,
    email: m.email,
    cycles: m.cycles,
    rollingScore: 0,
    currentCycle: m.current_cycle,
  };
}

export default function LeaderMissionView({ orgId, userId, role }: Props) {
  const { apiGet, apiPost } = useApi();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<MissionControlConfig | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [viewingMember, setViewingMember] = useState<TeamMember | null>(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberAccounts, setMemberAccounts] = useState<Account[]>([]);
  const [memberContacts, setMemberContacts] = useState<Contact[]>([]);
  const [memberConfig, setMemberConfig] = useState<MissionControlConfig | null>(null);

  const [activeTab, setActiveTab] = useState<'team' | 'objectives'>('team');
  const [myObjLoading, setMyObjLoading] = useState(false);
  const [myObjLoaded, setMyObjLoaded] = useState(false);
  const [myCycles, setMyCycles] = useState<MissionControlCycle[]>([]);
  const [myCurrentCycle, setMyCurrentCycle] = useState<EnrichedCycle | null>(null);
  const [myAccounts, setMyAccounts] = useState<Account[]>([]);
  const [myContacts, setMyContacts] = useState<Contact[]>([]);
  const [myContactPriorities, setMyContactPriorities] = useState<Record<string, string>>({});
  const [myManagerConfig, setMyManagerConfig] = useState<MissionControlConfig | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // The backend applies the executive→member filter and enriches each
      // report's active cycle server-side (matching the legacy leader RLS).
      const data = await apiGet<TeamResponse>(`/v1/mission-control/team?org_id=${orgId}`);
      setConfig(data.config);
      setTeamMembers((data.team_members || []).map(toTeamMember));
    } finally {
      setLoading(false);
    }
  }, [orgId, apiGet]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadMyObjectives = useCallback(async (force?: boolean) => {
    if (myObjLoaded && !force) return;
    setMyObjLoading(true);
    try {
      // The leader's own objectives use the same member overview endpoint; its
      // `config` is the caller's manager config — fall back to the leader's own
      // config when they have no manager (legacy loadMyObjectives behaviour).
      const data = await apiGet<OverviewResponse>(`/v1/mission-control/overview?org_id=${orgId}`);
      setMyCycles(data.cycles || []);
      setMyAccounts(data.accounts || []);
      setMyContacts(data.contacts || []);
      setMyContactPriorities(data.contact_priorities || {});
      setMyManagerConfig(data.config ?? config);
      setMyCurrentCycle(data.current_cycle);
      setMyObjLoaded(true);
    } finally {
      setMyObjLoading(false);
    }
  }, [orgId, apiGet, config, myObjLoaded]);

  function handleTabSwitch(tab: 'team' | 'objectives') {
    setActiveTab(tab);
    if (tab === 'objectives') loadMyObjectives();
  }

  function handleMyObjectivesRefresh() {
    loadMyObjectives(true);
  }

  const refreshViewingMember = useCallback(async (member: TeamMember) => {
    const data = await apiGet<TeamMemberDetailResponse>(
      `/v1/mission-control/team/${member.user_id}?org_id=${orgId}`,
    );
    const updated: TeamMember = {
      ...member,
      cycles: data.cycles || [],
      currentCycle: data.current_cycle,
    };
    setViewingMember(updated);
    setTeamMembers(prev => prev.map(m => m.user_id === member.user_id ? updated : m));
  }, [orgId, apiGet]);

  const handleViewMember = useCallback(async (member: TeamMember) => {
    setMemberLoading(true);
    try {
      // One call returns the member's accounts/contacts/cycles + enriched active
      // cycle and the caller's own leader config (server-derived authz).
      const data = await apiGet<TeamMemberDetailResponse>(
        `/v1/mission-control/team/${member.user_id}?org_id=${orgId}`,
      );
      setMemberAccounts(data.accounts || []);
      setMemberContacts(data.contacts || []);
      setMemberConfig(data.config);
      const updated: TeamMember = { ...member, cycles: data.cycles || [], currentCycle: data.current_cycle };
      setViewingMember(updated);
      setTeamMembers(prev => prev.map(m => m.user_id === member.user_id ? updated : m));
    } finally {
      setMemberLoading(false);
    }
  }, [orgId, apiGet]);

  // Apply a leader transition; the backend updates the cycle and notifies the
  // member atomically. On failure we leave state untouched (legacy `if (error) return`).
  async function applyLeaderAction(cycleId: string, payload: { action: string; notes?: string; score?: number }) {
    try {
      await apiPost(`/v1/mission-control/cycles/${cycleId}/leader-action`, payload);
    } catch {
      return;
    }
    if (viewingMember) {
      await refreshViewingMember(viewingMember);
    }
    loadData();
  }

  function handleApprove(cycleId: string) {
    return applyLeaderAction(cycleId, { action: 'approve' });
  }

  function handleRejectOrNote(cycleId: string, notes: string) {
    return applyLeaderAction(cycleId, { action: 'request_changes', notes });
  }

  function handleReviewComplete(cycleId: string, score: number, notes: string) {
    return applyLeaderAction(cycleId, { action: 'review', score, notes });
  }

  const teamAvgScore = useMemo(() => {
    const withScores = teamMembers.filter(m => {
      const scored = m.cycles.filter(c => (c.status === 'reviewed' || c.status === 'completed') && c.completion_score > 0);
      return scored.length > 0;
    });
    if (withScores.length === 0) return 0;
    const total = withScores.reduce((sum, m) => {
      const scored = m.cycles.filter(c => (c.status === 'reviewed' || c.status === 'completed') && c.completion_score > 0);
      const avg = scored.reduce((s, c) => s + c.completion_score, 0) / scored.length;
      return sum + avg;
    }, 0);
    return total / withScores.length;
  }, [teamMembers]);

  const pendingApprovals = useMemo(() =>
    teamMembers.filter(m => m.currentCycle?.status === 'submitted'),
    [teamMembers]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (memberLoading) {
    return (
      <div className="app-page">
        <div className="flex items-center justify-center h-40">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (viewingMember) {
    return (
      <div>
        <div className="px-8 pt-8">
          <button
            onClick={() => setViewingMember(null)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 mb-4 text-sm font-medium text-forest-700 bg-forest-50 rounded-lg hover:bg-forest-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Team View
          </button>
          <p className="text-sm text-app-muted mb-2">
            Viewing objectives for <span className="font-semibold text-app-ink">{viewingMember.name}</span>
          </p>

          {/* Approval actions for submitted cycles */}
          {viewingMember.currentCycle?.status === 'submitted' && (
            <ApprovalBar
              cycleName={`${new Date(viewingMember.currentCycle.cycle_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} cycle`}
              onApprove={() => handleApprove(viewingMember.currentCycle!.id)}
              onReject={(notes) => handleRejectOrNote(viewingMember.currentCycle!.id, notes)}
            />
          )}

          {/* Approved confirmation */}
          {viewingMember.currentCycle?.status === 'approved' && (
            <div className="bg-forest-50 rounded-lg border border-forest-200 p-4 mb-4 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-forest-600 shrink-0" />
              <p className="text-sm text-forest-800">
                This cycle has been approved. The member can now start executing on their objectives.
              </p>
            </div>
          )}

          {/* Review actions for completed cycles */}
          {(viewingMember.currentCycle?.status === 'completed' || viewingMember.currentCycle?.status === 'in_progress') && (
            <ReviewBar
              cycle={viewingMember.currentCycle}
              onReview={(score, notes) => handleReviewComplete(viewingMember.currentCycle!.id, score, notes)}
            />
          )}
        </div>

        <MemberMissionView
          config={memberConfig}
          myCycles={viewingMember.cycles}
          currentCycle={viewingMember.currentCycle}
          myAccounts={memberAccounts}
          myContacts={memberContacts}
          orgId={orgId}
          userId={viewingMember.user_id}
          editable={false}
          onRefresh={() => {
            if (viewingMember) refreshViewingMember(viewingMember);
            loadData();
          }}
        />
      </div>
    );
  }

  return (
    <div className="app-page">
      <div className="mb-6">
        <p className="app-kicker mb-2">Team execution</p>
        <h1 className="font-display text-3xl font-semibold text-app-ink sm:text-4xl">Mission Control</h1>
        <p className="text-sm text-app-muted">
          {role === 'executive' ? 'Review team member objectives and progress' : 'Manage your team\'s prospecting objectives'}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-app-line">
        <button
          onClick={() => handleTabSwitch('team')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'team'
              ? 'border-forest-600 text-forest-700'
              : 'border-transparent text-app-muted hover:text-app-ink'
          }`}
        >
          My Team
          {pendingApprovals.length > 0 && (
            <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-app-warning rounded-full">
              {pendingApprovals.length}
            </span>
          )}
        </button>
        <button
          onClick={() => handleTabSwitch('objectives')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'objectives'
              ? 'border-forest-600 text-forest-700'
              : 'border-transparent text-app-muted hover:text-app-ink'
          }`}
        >
          My Objectives
        </button>
      </div>

      {activeTab === 'objectives' && (
        myObjLoading ? (
          <div className="flex items-center justify-center h-40">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <div className="-mx-8 -mt-2">
            <MemberMissionView
              config={myManagerConfig}
              myCycles={myCycles}
              currentCycle={myCurrentCycle}
              myAccounts={myAccounts}
              myContacts={myContacts}
              contactPriorities={myContactPriorities}
              orgId={orgId}
              userId={userId}
              onRefresh={handleMyObjectivesRefresh}
              overrideLeaderId={userId}
            />
          </div>
        )
      )}

      {activeTab === 'team' && (
        <div className="space-y-6">
          {role !== 'executive' && (
            <ConfigPanel
              config={config}
              orgId={orgId}
              userId={userId}
              onSaved={c => setConfig(c)}
            />
          )}

          {/* Summary stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-app-surface rounded-lg border border-app-line p-4">
              <p className="text-xs text-app-muted">Team Members</p>
              <p className="text-2xl font-bold text-app-ink mt-1">{teamMembers.length}</p>
            </div>
            <div className="bg-app-surface rounded-lg border border-app-line p-4">
              <p className="text-xs text-app-muted">Pending Approval</p>
              <p className="text-2xl font-bold text-app-warning mt-1">{pendingApprovals.length}</p>
            </div>
            <div className="bg-app-surface rounded-lg border border-app-line p-4">
              <p className="text-xs text-app-muted">Team Avg Score</p>
              <p className={`text-2xl font-bold mt-1 ${teamAvgScore >= 80 ? 'text-forest-600' : teamAvgScore >= 50 ? 'text-app-warning' : 'text-app-muted'}`}>
                {teamAvgScore > 0 ? `${Math.round(teamAvgScore)}%` : 'N/A'}
              </p>
            </div>
            <div className="bg-app-surface rounded-lg border border-app-line p-4">
              <p className="text-xs text-app-muted">Active Objectives</p>
              <p className="text-2xl font-bold text-forest-600 mt-1">
                {teamMembers.filter(m => m.currentCycle && m.currentCycle.status !== 'reviewed').length}
              </p>
            </div>
          </div>

          {/* Team cards grid */}
          {teamMembers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamMembers.map(m => (
                <TeamMemberCard
                  key={m.user_id}
                  name={m.name}
                  email={m.email}
                  cycles={m.cycles}
                  currentCycle={m.currentCycle}
                  onClick={() => handleViewMember(m)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-sm text-app-muted">
                {role === 'executive'
                  ? 'No direct member reports found. This view shows members (not leaders) reporting to you.'
                  : 'No direct reports found. Team members need to be assigned to you via the manager hierarchy.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ApprovalBar({ cycleName, onApprove, onReject }: {
  cycleName: string;
  onApprove: () => void;
  onReject: (notes: string) => void;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  async function handleApprove() {
    setApproving(true);
    await onApprove();
    setApproving(false);
  }

  async function handleReject() {
    setRejecting(true);
    await onReject(notes);
    setRejecting(false);
    setRejectOpen(false);
  }

  return (
    <div className="bg-app-focus/10 rounded-lg border border-app-focus p-4 mb-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-app-focus">
          <span className="font-medium">{cycleName}</span> is awaiting your approval
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRejectOpen(!rejectOpen)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-app-critical bg-app-surface border border-app-critical rounded-lg hover:bg-app-critical/10 transition-colors"
          >
            <XCircle className="w-4 h-4" />
            Request Changes
          </button>
          <button
            onClick={handleApprove}
            disabled={approving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-forest-600 rounded-lg hover:bg-forest-700 disabled:opacity-50 transition-colors"
          >
            {approving ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            Approve
          </button>
        </div>
      </div>
      {rejectOpen && (
        <div className="mt-3 flex items-center gap-2">
          <input
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Add feedback..."
            className="flex-1 rounded-lg border border-app-focus px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/20"
          />
          <button
            onClick={handleReject}
            disabled={rejecting}
            className="px-3 py-2 text-sm font-medium text-white bg-app-critical rounded-lg hover:bg-app-critical disabled:opacity-50 transition-colors"
          >
            {rejecting ? 'Sending...' : 'Send'}
          </button>
        </div>
      )}
    </div>
  );
}

function ReviewBar({ cycle, onReview }: {
  cycle: EnrichedCycle;
  onReview: (score: number, notes: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [score, setScore] = useState(() => {
    const totalContacts = cycle.objectives.reduce((s, o) => s + o.contacts.length, 0);
    const completed = cycle.objectives.reduce((s, o) => s + o.contacts.filter(c => c.status === 'completed').length, 0);
    return totalContacts > 0 ? Math.round((completed / totalContacts) * 100) : 0;
  });
  const [notes, setNotes] = useState('');

  async function handleSubmit() {
    setSubmitting(true);
    await onReview(score, notes);
    setSubmitting(false);
  }

  return (
    <div className="bg-forest-50 rounded-lg border border-forest-200 p-4 mb-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-forest-800">
          This cycle is ready for review
        </p>
        <button
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-forest-600 rounded-lg hover:bg-forest-700 transition-colors"
        >
          <MessageSquare className="w-4 h-4" />
          Review & Score
        </button>
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-forest-700 w-24">Score (0-100)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={score}
              onChange={e => setScore(Number(e.target.value))}
              className="w-20 rounded-lg border border-forest-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/20"
            />
          </div>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Leader notes..."
            rows={2}
            className="w-full rounded-lg border border-forest-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/20 resize-none"
          />
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-forest-600 rounded-lg hover:bg-forest-700 disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </div>
      )}
    </div>
  );
}
